import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export type AssetThumbKind = 'image' | 'video' | 'empty';
export type AssetThumbState =
  | 'empty'
  | 'queued'
  | 'generating'
  | 'ready'
  | 'error';

export type AssetThumbProps = {
  kind?: AssetThumbKind;
  src?: string;
  posterSrc?: string;
  alt?: string;
  state?: AssetThumbState;
  aspect?: '9:16' | '16:9' | '1:1' | '4:5';
  selected?: boolean;
  locked?: boolean;
  hoverPlay?: boolean;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  errorLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
};

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const aspectRatio: Record<NonNullable<AssetThumbProps['aspect']>, string> = {
  '9:16': '9 / 16',
  '16:9': '16 / 9',
  '1:1': '1 / 1',
  '4:5': '4 / 5',
};

type FrameProps = {
  $aspect: NonNullable<AssetThumbProps['aspect']>;
  $selected: boolean;
  $clickable: boolean;
  $state: AssetThumbState;
};

const Frame = styled.div<FrameProps>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => aspectRatio[p.$aspect]};
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--color-surface-inset);
  border: 1px solid
    ${(p) => (p.$selected ? 'var(--color-accent)' : 'var(--color-border-subtle)')};
  box-shadow: ${(p) =>
    p.$selected ? '0 0 0 3px var(--color-focus-ring)' : 'var(--shadow-xs)'};
  transition:
    border-color var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard),
    transform var(--duration-base) var(--ease-standard);

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      &:hover {
        border-color: var(--color-border-strong);
        transform: translateY(-1px);
        box-shadow: var(--shadow-sm);
      }
      &:active {
        transform: translateY(0);
      }
    `}

  ${(p) =>
    p.$state === 'generating' &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.18) 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.4s ease-in-out infinite;
        pointer-events: none;
      }
    `}
`;

const Media = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    animation: ${fadeIn} var(--duration-base) var(--ease-standard);
  }
`;

const EmptyMark = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--space-2);
  pointer-events: none;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-2);
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: flex-end;
  pointer-events: auto;
`;

const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: var(--space-2) var(--space-3);
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.65) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  color: #fff;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  pointer-events: none;
`;

const StatusPill = styled.span<{ $tone: 'neutral' | 'danger' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'danger'
      ? 'var(--color-danger)'
      : p.$tone === 'accent'
      ? 'var(--color-accent)'
      : 'rgba(0, 0, 0, 0.55)'};
  color: #fff;
`;

const LockChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  pointer-events: none;
`;

export const AssetThumb = React.forwardRef<HTMLDivElement, AssetThumbProps>(
  (
    {
      kind = 'image',
      src,
      posterSrc,
      alt,
      state = 'ready',
      aspect = '9:16',
      selected = false,
      locked = false,
      hoverPlay = true,
      badge,
      footer,
      errorLabel = 'Failed',
      onClick,
      className,
    },
    ref
  ) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);

    const handleEnter = () => {
      if (kind === 'video' && hoverPlay && videoRef.current) {
        void videoRef.current.play().catch(() => {});
      }
    };
    const handleLeave = () => {
      if (kind === 'video' && hoverPlay && videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };

    const effectiveKind: AssetThumbKind = src ? kind : 'empty';

    return (
      <Frame
        ref={ref}
        className={className}
        $aspect={aspect}
        $selected={selected}
        $clickable={Boolean(onClick)}
        $state={state}
        onClick={onClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Media>
          {effectiveKind === 'image' && src && <img src={src} alt={alt ?? ''} />}
          {effectiveKind === 'video' && src && (
            <video
              ref={videoRef}
              src={src}
              poster={posterSrc}
              muted
              playsInline
              loop
              preload="metadata"
            />
          )}
          {effectiveKind === 'empty' && <EmptyMark>{state === 'queued' ? 'Queued' : 'Empty'}</EmptyMark>}
        </Media>

        <Overlay>
          <TopRow>
            <div>{badge}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', pointerEvents: 'auto' }}>
              {state === 'generating' && <StatusPill $tone="accent">Generating</StatusPill>}
              {state === 'error' && <StatusPill $tone="danger">{errorLabel}</StatusPill>}
              {locked && <LockChip aria-label="Locked">🔒</LockChip>}
            </div>
          </TopRow>
          <BottomRow />
        </Overlay>

        {footer && <Footer>{footer}</Footer>}
      </Frame>
    );
  }
);

AssetThumb.displayName = 'AssetThumb';
